import { FindingList } from 'components/ai/FindingList';
import { CallForActionButtonTooltip } from 'components/buttons/CallForActionButton';
import { FIELD_GLYPH_BUTTON_CLASS, OFF_ACTION_CLASS } from 'components/buttons/buttonStyle';
import {
    ArrowRightIcon,
    AutoAwesome,
    CheckIcon,
    CircleAlert,
    CircleCheck,
    InfoOutlined,
    Loader2Icon,
    PlusIcon,
    TriangleAlert,
    X,
} from 'components/icons';
import { Button } from 'components/ui/button';
import { NEW_FOCUS_RING } from 'layout/NewDesignLayout/focusRing';
import { AnswerRating, PostRating, SentNote, usePostFeedback } from 'pages/ai-chat/components/AiChatFeedback';
import { AI_MONTHLY_USAGE_LIMIT_EXCEEDED_CODE } from 'pages/ai-chat/constants/errorMessages';
import { useFloatingAiAvailable } from 'pages/ai-chat/floating/FloatingAiAssistant';
import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { Control, UseFormGetValues, UseFormSetFocus, useWatch } from 'react-hook-form';
import { readApiErrorBody } from 'store/api/apiError';
import { useImproveTimecardDescriptionMutation } from 'store/api/timecards';
import { ImprovedTimecardDescription, TimecardDto } from 'types/timecards';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import {
    captureDescriptionSuggestionFailed,
    captureDescriptionSuggestionReceived,
    captureDescriptionSuggestionUndone,
    captureDescriptionSuggestionUsed,
    DescriptionSuggestionMode,
    DescriptionVia,
} from 'utils/timecardAnalytics';
import { DescriptionRulesDialog } from './DescriptionRulesDialog';
import { DescriptionAiEdit } from './useDescriptionAiEdit';

type Props = {
    control: Control<TimecardDto>;
    getValues: UseFormGetValues<TimecardDto>;
    /** Gives the field focus back after Undo, which removes the line it was on. */
    setFocus: UseFormSetFocus<TimecardDto>;
    /** The description's AI edits, which "Use this text" writes through. */
    aiEdit: DescriptionAiEdit;
    /** Whether the text reads as another language than the timecard's: the button then translates too. */
    isMismatch: boolean;
    /** Checks the language at once, as the field's blur does. */
    checkDescription: () => void;
    /** The case picked, whose timecard language and matter type the AI is told when the user may read it. */
    matterId?: number | null;
    /** The language the text is checked against, which the AI is told instead for a case the user may not read. */
    timecardLanguageCode?: string | null;
    /** Where the description is written, as its suggestion events say. */
    via: DescriptionVia;
    /**
     * Lays out the field: the row and the answer box go under it, as wide as its text, and the rules button by its
     * label. The row holds the status line, so it is rendered from the first render on; the box and the rules
     * button are null while there is none.
     */
    children: (row: ReactNode, box: ReactNode, rulesButton: JSX.Element | null) => ReactNode;
};

/** A request and, once it is in, its answer. */
type Asked = {
    id: number;
    text: string;
    matterId: number | null;
    mode: DescriptionSuggestionMode;
    answer: ImprovedTimecardDescription | null;
};

export type Outcome = 'NEEDS_DETAIL' | 'IMPROVED' | 'CONSIDER' | 'NO_CHANGES';

const TITLE_KEYS: Record<Outcome, string> = {
    NEEDS_DETAIL: 'timecards.form.description-needs-detail',
    IMPROVED: 'timecards.form.description-suggestion',
    CONSIDER: 'timecards.form.description-consider-title',
    NO_CHANGES: 'timecards.form.description-no-changes-title',
};

const MUTED_CLASS = 'text-grey-600 new:text-muted-foreground';
// Each tone keeps its colour on hover too, over the kit outline's foreground.
const AI_TONE =
    'border-ai-soft bg-paper text-ai hover:bg-ai-soft hover:text-ai aria-expanded:bg-ai-soft aria-expanded:text-ai new:bg-ai-wash new:text-ai-ink new:hover:bg-ai-soft new:hover:text-ai-ink new:aria-expanded:bg-ai-soft new:aria-expanded:text-ai-ink';
const WARNING_TONE =
    'border-warning-border bg-warning-light text-warning-darker hover:bg-warning-border/60 hover:text-warning-darker aria-expanded:bg-warning-border/60 aria-expanded:text-warning-darker new:bg-warning-tint new:text-warning-ink new:hover:bg-warning-border/60 new:hover:text-warning-ink new:aria-expanded:bg-warning-border/60 new:aria-expanded:text-warning-ink';
/** Off for an empty field: the fill and the label dim, never the button's opacity. */
const OFF_TONE = cn(
    OFF_ACTION_CLASS,
    'aria-disabled:border-divider aria-disabled:bg-grey-100 aria-disabled:text-grey-500 aria-disabled:hover:bg-grey-100 aria-disabled:hover:text-grey-500 aria-disabled:shadow-none',
    'new:aria-disabled:border-border new:aria-disabled:bg-surface-subtle new:aria-disabled:text-muted-foreground new:aria-disabled:hover:bg-surface-subtle new:aria-disabled:hover:text-muted-foreground'
);

/** A proposal that only echoes the text changes nothing; what it cannot fix is still worth a box. */
function outcomeOf(answer: ImprovedTimecardDescription, text: string): Outcome {
    if (answer.proposal && answer.proposal.trim() !== text.trim()) {
        return 'IMPROVED';
    }
    if (answer.missing.length > 0) {
        return 'NEEDS_DETAIL';
    }
    return answer.alsoConsider.length > 0 ? 'CONSIDER' : 'NO_CHANGES';
}

type AnswerBoxProps = {
    id: string;
    outcome: Outcome;
    answer: ImprovedTimecardDescription;
    onClose: () => void;
    onUse: (proposal: string) => void;
    onShowRules: () => void;
    /** Posts a rating of the answer. */
    onRate: PostRating;
    /** Says "Feedback sent" through the field's status line. */
    onRated: () => void;
};

/**
 * The AI's answer under the field's row: one box whose look and content follow the outcome. It ends in the rules
 * footer and the answer's thumbs; a rating touches neither the text nor the timecard, and lives as long as the box.
 */
function AnswerBox({ id, outcome, answer, onClose, onUse, onShowRules, onRate, onRated }: AnswerBoxProps) {
    const titleId = useId();
    const isWarning = outcome === 'NEEDS_DETAIL' || outcome === 'CONSIDER';
    const isProposal = outcome === 'IMPROVED';
    // The × is named as the box's other way out.
    const closeLabel = isProposal ? t('timecards.form.description-suggestion-dismiss') : t('close');

    return (
        <section
            id={id}
            aria-labelledby={titleId}
            onKeyDown={(event) => {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    onClose();
                }
            }}
            className={cn(
                'mt-2 flex flex-col gap-3 rounded-lg border p-4',
                isWarning && 'bg-warning-light border-warning-border new:bg-warning-tint',
                isProposal && 'border-ai-soft bg-paper shadow-card-raised',
                outcome === 'NO_CHANGES' && 'bg-success-light border-success-border new:bg-success-tint'
            )}
        >
            <div className="flex items-center gap-2">
                {isWarning && <TriangleAlert className="text-warning size-4" />}
                {isProposal && <AutoAwesome className="text-ai size-4" />}
                {outcome === 'NO_CHANGES' && <CircleCheck className="text-success new:text-success-ink size-4" />}
                <h3 id={titleId} className="text-sm font-semibold">
                    {t(TITLE_KEYS[outcome])}
                </h3>
                <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={closeLabel}
                    onClick={onClose}
                    className={cn(MUTED_CLASS, 'ml-auto size-7')}
                >
                    <X />
                </Button>
            </div>
            {outcome === 'NEEDS_DETAIL' && (
                <>
                    <p className="text-sm">{t('timecards.form.description-needs-detail-lead')}</p>
                    <FindingList
                        rules={answer.missing}
                        Marker={PlusIcon}
                        markerClassName="text-warning"
                        withStop={false}
                        className="gap-1.5"
                    />
                </>
            )}
            {isProposal && answer.proposal && (
                <>
                    <p className="bg-ai-soft/40 new:bg-ai-wash rounded-md px-3 py-2.5 text-sm break-words whitespace-pre-wrap">
                        {answer.proposal}
                    </p>
                    {answer.fixes.length > 0 && (
                        <div className="flex flex-col gap-1.5">
                            <h4 className="text-success-darker new:text-success-ink text-caption font-semibold">
                                {t('timecards.form.description-suggestion-changes')}
                            </h4>
                            <FindingList
                                rules={answer.fixes}
                                Marker={ArrowRightIcon}
                                markerClassName="text-success new:text-success-ink"
                            />
                        </div>
                    )}
                    {answer.alsoConsider.length > 0 && (
                        <div className="flex flex-col gap-1.5">
                            <h4 className="text-warning-darker new:text-warning-ink text-caption font-semibold">
                                {t('timecards.form.description-suggestion-consider')}
                            </h4>
                            <FindingList
                                rules={answer.alsoConsider}
                                Marker={CircleAlert}
                                markerClassName="text-warning"
                            />
                        </div>
                    )}
                </>
            )}
            {outcome === 'CONSIDER' && (
                <>
                    <p className="text-sm">{t('timecards.form.description-consider-lead')}</p>
                    <FindingList
                        rules={answer.alsoConsider}
                        Marker={CircleAlert}
                        markerClassName="text-warning"
                        className="gap-1.5"
                    />
                </>
            )}
            {outcome === 'NO_CHANGES' && <p className="text-sm">{t('timecards.form.description-no-changes-body')}</p>}
            <div className="flex flex-wrap items-center gap-2">
                {isProposal && answer.proposal && (
                    <Button
                        size="sm"
                        onClick={() => onUse(answer.proposal ?? '')}
                        className="bg-ai text-primary-foreground hover:bg-ai/80 new:bg-ai-ink font-semibold"
                    >
                        <CheckIcon data-icon="inline-start" />
                        {t('timecards.form.description-suggestion-use')}
                    </Button>
                )}
                <Button
                    size="sm"
                    variant="outline"
                    onClick={onClose}
                    className={cn(
                        isWarning && 'border-warning-border',
                        outcome === 'NO_CHANGES' && 'border-success-border'
                    )}
                >
                    {closeLabel}
                </Button>
            </div>
            <AnswerRating post={onRate} announce={onRated} reveal>
                {(thumbs, sent) => (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <p className={cn(MUTED_CLASS, 'text-caption min-w-0 flex-[1_1_12rem]')}>
                            {t(
                                answer.firmRules
                                    ? 'timecards.form.description-suggestion-rules-firm'
                                    : 'timecards.form.description-suggestion-rules'
                            )}{' '}
                            <button
                                type="button"
                                onClick={onShowRules}
                                className={cn(
                                    'text-fg new:text-foreground new:underline-offset-auto cursor-pointer font-semibold underline underline-offset-3 hover:no-underline',
                                    NEW_FOCUS_RING
                                )}
                            >
                                {t('timecards.form.description-show-rules')}
                            </button>
                        </p>
                        {/* Held at one width, sent or not, so the row wraps alike and the thumbs never move under
                            the pointer; keyed, the note rises again each time it shows. */}
                        <div className="ms-auto flex items-center gap-1 pointer-coarse:gap-4">
                            <SentNote key={String(sent)} hidden={!sent} className={cn(MUTED_CLASS, 'me-1.5')} />
                            {thumbs}
                        </div>
                    </div>
                )}
            </AnswerRating>
        </section>
    );
}

/**
 * What the status line says: the warning as it appears, the work while it runs, then the answer's title, and the
 * "Improved by AI" line once its text is in the field.
 */
function statusOf(isMismatch: boolean, isLoading: boolean, outcome: Outcome | null, isAiText: boolean): string {
    if (isLoading) {
        return t('timecards.form.description-improving');
    }
    if (outcome) {
        return t(TITLE_KEYS[outcome]);
    }
    if (isMismatch) {
        return t('timecards.form.description-language-mismatch');
    }
    return isAiText ? t('timecards.form.description-improved') : '';
}

/**
 * Everything the description's AI shows, under the field: a row with the language warning or the "Improved by AI ·
 * Undo" line on the left and, with the AI chat open to the user, "Improve with AI" on the right, which on a language
 * mismatch translates too; the AI's answer in one box under it; and a button that opens the rules. The answer is
 * asked for on a click only; "Use this text" puts the proposal in the field, unsaved, and checks its language at
 * once. A failed call is a toast and leaves the field as it is.
 */
export function DescriptionImprove({
    control,
    getValues,
    setFocus,
    aiEdit,
    isMismatch,
    checkDescription,
    matterId,
    timecardLanguageCode,
    via,
    children,
}: Props) {
    const canImprove = useFloatingAiAvailable();
    const description = useWatch({ control, name: 'description' });
    const [improve] = useImproveTimecardDescriptionMutation();
    const [asked, setAsked] = useState<Asked | null>(null);
    // The answer rated, and how often: each send is said again.
    const [sent, setSent] = useState<{ askedId: number; count: number } | null>(null);
    const postFeedback = usePostFeedback();
    const [isRulesOpen, setIsRulesOpen] = useState(false);
    const lastAskedId = useRef(0);
    // The suggestion used last, whose text Undo takes back.
    const usedMode = useRef<DescriptionSuggestionMode | null>(null);
    const currentMatterId = useRef(matterId ?? null);
    const button = useRef<HTMLButtonElement>(null);
    const boxId = useId();
    const blankReasonId = useId();

    useEffect(() => {
        currentMatterId.current = matterId ?? null;
    }, [matterId]);

    // However the box closed, the answer on its way is dropped: no late toast, no reopening.
    useEffect(() => {
        if (asked === null) {
            lastAskedId.current += 1;
        }
    }, [asked]);

    // Typing, History, a template or a case change closes the box: it is of a text or a case the form no longer
    // holds.
    if (asked && (asked.text !== description || asked.matterId !== (matterId ?? null))) {
        setAsked(null);
    }

    const isLoading = canImprove && asked !== null && asked.answer === null;
    const outcome = canImprove && asked?.answer ? outcomeOf(asked.answer, asked.text) : null;
    const isBlank = !description?.trim();

    const close = () => {
        lastAskedId.current += 1;
        setAsked(null);
        button.current?.focus();
    };

    const requestImprovement = async () => {
        const text = getValues('description');
        const askedMatterId = matterId ?? null;
        const mode = isMismatch ? 'translate' : 'improve';
        lastAskedId.current += 1;
        const id = lastAskedId.current;
        setAsked({ id, text, matterId: askedMatterId, mode, answer: null });
        try {
            const answer = await improve({
                text,
                matterId: askedMatterId,
                languageCode: timecardLanguageCode ?? null,
            }).unwrap();
            // Sent even for an answer dropped below: the request was made and its usage spent.
            captureDescriptionSuggestionReceived({ mode, outcome: outcomeOf(answer, text), via, timecardLanguageCode });
            // Asked again, or the text or the case changed meanwhile.
            if (
                lastAskedId.current !== id ||
                getValues('description') !== text ||
                currentMatterId.current !== askedMatterId
            ) {
                return;
            }
            setAsked({ id, text, matterId: askedMatterId, mode, answer });
        } catch (error) {
            const isLimit = readApiErrorBody(error)?.code === AI_MONTHLY_USAGE_LIMIT_EXCEEDED_CODE;
            captureDescriptionSuggestionFailed({ mode, reason: isLimit ? 'usage_limit' : 'error', via });
            if (lastAskedId.current === id) {
                setAsked(null);
                errorAlert(
                    t(
                        isLimit
                            ? 'timecards.form.description-improve-limit'
                            : 'timecards.form.description-improve-failed'
                    )
                );
            }
        }
    };

    const blankReason = t('timecards.form.description-improve-blank');
    const improveButton = canImprove && (
        <CallForActionButtonTooltip title={isBlank ? blankReason : null}>
            <Button
                ref={button}
                size="sm"
                variant="outline"
                aria-expanded={outcome !== null}
                aria-controls={outcome ? boxId : undefined}
                aria-busy={isLoading || undefined}
                aria-describedby={isBlank ? blankReasonId : undefined}
                // Off but focusable: it keeps the focus while it works, and its tooltip says why it is off.
                disabled={isBlank || isLoading}
                focusableWhenDisabled
                onClick={requestImprovement}
                className={cn(
                    'ms-auto font-semibold',
                    isMismatch ? WARNING_TONE : AI_TONE,
                    isBlank && OFF_TONE,
                    isLoading && 'aria-disabled:cursor-wait'
                )}
            >
                {isLoading ? (
                    <Loader2Icon
                        data-icon="inline-start"
                        className="size-4 motion-safe:animate-spin motion-reduce:animate-pulse"
                    />
                ) : (
                    <AutoAwesome data-icon="inline-start" className="size-4" />
                )}
                {t(isMismatch ? 'timecards.form.description-translate-improve' : 'timecards.form.description-improve')}
            </Button>
        </CallForActionButtonTooltip>
    );

    const row = (
        <>
            {/* Mounted from the first render and empty until something changes: a live region that arrives with
                its words is not reliably read. */}
            <p role="status" className="sr-only">
                {sent && sent.askedId === asked?.id && outcome ? (
                    <span key={sent.count}>{t('ai-chat.feedback.sent')}</span>
                ) : (
                    statusOf(isMismatch, isLoading, outcome, aiEdit.isAiText)
                )}
            </p>
            {(canImprove || isMismatch) && (
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
                    {isMismatch && (
                        <p className="text-warning-darker new:text-warning-ink flex min-w-0 flex-[1_1_12rem] items-center gap-1.5 text-sm font-medium">
                            <TriangleAlert className="size-4" />
                            {t('timecards.form.description-language-mismatch')}
                        </p>
                    )}
                    {aiEdit.isAiText && (
                        <div
                            className={cn(
                                MUTED_CLASS,
                                'flex h-7 min-w-0 flex-[1_1_12rem] items-center gap-1.5 text-sm'
                            )}
                        >
                            <AutoAwesome className="text-ai size-3.5" />
                            <span>{t('timecards.form.description-improved')}</span>
                            <span aria-hidden="true">·</span>
                            <button
                                type="button"
                                // Undo removes the line it sits in, so focus goes to the text it put back.
                                onClick={() => {
                                    aiEdit.undo();
                                    setFocus('description');
                                    if (usedMode.current) {
                                        captureDescriptionSuggestionUndone({ mode: usedMode.current, via });
                                    }
                                }}
                                className={cn(
                                    'text-ai new:underline-offset-auto cursor-pointer font-semibold underline underline-offset-3 hover:no-underline',
                                    NEW_FOCUS_RING
                                )}
                            >
                                {t('timecards.form.description-translate-undo')}
                            </button>
                        </div>
                    )}
                    {improveButton}
                    {canImprove && isBlank && (
                        <span id={blankReasonId} hidden>
                            {blankReason}
                        </span>
                    )}
                </div>
            )}
            {canImprove && <DescriptionRulesDialog open={isRulesOpen} onOpenChange={setIsRulesOpen} />}
        </>
    );

    const box = outcome && asked?.answer && (
        <AnswerBox
            id={boxId}
            outcome={outcome}
            answer={asked.answer}
            onClose={close}
            onUse={(proposal) => {
                aiEdit.apply(proposal, getValues('description'));
                usedMode.current = asked.mode;
                captureDescriptionSuggestionUsed({ mode: asked.mode, via });
                close();
                checkDescription();
            }}
            onShowRules={() => setIsRulesOpen(true)}
            // An echo of the text is no proposal, so only a suggested text has one to keep.
            onRate={(sentiment, category, comment) =>
                postFeedback({
                    source: 'IMPROVE',
                    timecardId: getValues('id') ?? null,
                    textBefore: asked.text,
                    textAfter: outcome === 'IMPROVED' ? (asked.answer?.proposal ?? null) : null,
                    sentiment,
                    category,
                    comment,
                })
            }
            onRated={() => setSent((previous) => ({ askedId: asked.id, count: (previous?.count ?? 0) + 1 }))}
        />
    );

    const rulesButton = canImprove ? (
        // Named on hover and focus, as the History glyph beside it in the Timecard center is.
        <CallForActionButtonTooltip title={t('timecards.form.description-rules-title')}>
            <button
                type="button"
                aria-label={t('timecards.form.description-rules-title')}
                onClick={() => setIsRulesOpen(true)}
                className={FIELD_GLYPH_BUTTON_CLASS}
            >
                <InfoOutlined className="h-5 w-auto" />
            </button>
        </CallForActionButtonTooltip>
    ) : null;

    return children(row, box || null, rulesButton);
}
